import ReactECharts from 'echarts-for-react';

/**
 * The 98% cost gap — a WATERFALL / bridge chart (fully staffed $1.185M → savings
 * −$1.161M → Raisy $24K), built with ECharts' native stacked-bar waterfall
 * technique (transparent "base" series + visible "value" series).
 *
 * Lesson applied: identify the chart type FIRST (waterfall), then build it the
 * way the engine intends — do NOT hand-place annotation boxes on top of a bar
 * chart (that was the previous buggy approach).
 *
 * Brand colors from theme.ts.
 */

const BRAND = {
  teal: '#0AB3BF',
  tealLight: '#4DD4E0',
  tealDark: '#087B8B',
  red: '#EF4444',
  green: '#10B981',
  orange: '#F59E0B',
  orangeLight: '#FBBF24',
  slate: '#94A3B8',
  axis: '#334155',
  muted: '#64748B',
};

const HUMAN = 1185000;
const RAISY = 24000;
const SAVINGS = HUMAN - RAISY; // 1,161,000 (98%)

/**
 * ONE shared money formatter — used by the tooltip (hover) AND every bar/mark
 * label (non-hover) so the two states can never disagree on numbers, units, or
 * precision. Absolute-consistency rule (RULES_BLOG_POST chart-type-first).
 */
const formatMoney = (v: number): string => {
  const sign = v < 0 ? '−' : '';
  const a = Math.abs(v);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a}`;
};

export const CostGapChart = ({ height = 340 }: { height?: number }) => {
  const option = {
    // explicitly dark-opaque — labels/tooltip are light-on-dark styled, so the
    // chart must carry its own dark surface on the light blog page
    backgroundColor: '#0F172A',
    tooltip: {
      trigger: 'item',
      backgroundColor: '#1E293B',
      borderColor: BRAND.axis,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (p: { dataIndex: number }) => {
        const labels = ['Fully staffed', 'Savings (98%)', 'Raisy department'];
        const vals = [HUMAN, -SAVINGS, RAISY];
        return `${labels[p.dataIndex]}<br/><b>${formatMoney(vals[p.dataIndex])}</b>`;
      },
    },
    grid: { left: 50, right: 24, top: 30, bottom: 44 },
    xAxis: {
      type: 'category',
      data: ['Fully staffed', 'Savings (98%)', 'Raisy department'],
      axisLine: { lineStyle: { color: BRAND.axis } },
      axisLabel: { color: BRAND.slate, fontSize: 13, fontWeight: 600 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'annual cost (USD)',
      nameTextStyle: { color: BRAND.muted, fontSize: 11 },
      axisLabel: {
        color: BRAND.slate,
        // axis ticks use the SAME formatter too
        formatter: (v: number) => formatMoney(v),
      },
      splitLine: { lineStyle: { color: BRAND.axis, opacity: 0.35 } },
    },
    series: [
      // invisible base keeps the running total: after fully-staffed, the drop lands at RAISY
      {
        name: 'base',
        type: 'bar',
        stack: 'waterfall',
        itemStyle: { color: 'transparent', borderColor: 'transparent' },
        silent: true,
        data: [0, HUMAN, 0],
      },
      {
        name: 'value',
        type: 'bar',
        stack: 'waterfall',
        barWidth: '46%',
        data: [
          { value: HUMAN, itemStyle: { color: BRAND.red, borderRadius: [8, 8, 0, 0] } },
          // negative → drawn downward from the running total (the savings drop)
          {
            value: -SAVINGS,
            itemStyle: { color: BRAND.orange, borderRadius: 0 },
            label: { show: true, position: 'inside', color: '#0B0F1A', fontWeight: 700, fontSize: 13, formatter: '98% saved' },
          },
          { value: RAISY, itemStyle: { color: BRAND.green, borderRadius: [8, 8, 0, 0] } },
        ],
        label: {
          show: true,
          position: 'top',
          color: '#FFFFFF',
          fontWeight: 'bold',
          fontSize: 14,
          // bar labels use the SAME formatter as the tooltip
          formatter: (p: { dataIndex: number }) =>
            p.dataIndex === 1 ? '' : formatMoney(p.dataIndex === 0 ? HUMAN : RAISY),
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: BRAND.slate, type: 'dashed' },
          label: {
            color: BRAND.muted,
            fontSize: 11,
            // mark line sits at the RAISY level — its label matches its own position
            formatter: `you pay ${formatMoney(RAISY)}`,
          },
          data: [{ yAxis: RAISY }],
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-900 p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. The 98% cost gap as a waterfall — fully staffed $1.185M drops by the savings to Raisy $24K. Hover for values.
      </p>
    </div>
  );
};
